import type { CommunicationChannel } from "@/api/communications";
import { ChannelIcon, CHANNEL_LABELS } from "./ChannelIcon";
import { cn } from "@/design-system/lib/utils";

interface Props {
  value: CommunicationChannel | null;
  onChange: (next: CommunicationChannel | null) => void;
  /** Hide channels not relevant to the surface (e.g. tab panel may show all). */
  channels?: CommunicationChannel[];
  className?: string;
}

const DEFAULT_CHANNELS: CommunicationChannel[] = [
  "Call",
  "Voicemail",
  "Email",
  "Sms",
  "Meeting",
  "WebForm",
];

/**
 * Segmented "All / Calls / Voicemails / Emails / SMS / Meetings"
 * control. Per Comm Log Standard §9.3, the entity tab embeds this and
 * persists selection via useSearchParams. The component itself is
 * controlled — URL persistence is the caller's responsibility.
 */
export function ChannelFilterChips({
  value,
  onChange,
  channels = DEFAULT_CHANNELS,
  className,
}: Props) {
  return (
    <div
      className={cn("flex flex-wrap items-center gap-space-2", className)}
      role="radiogroup"
      aria-label="Filter by channel"
    >
      <Chip
        label="All"
        active={value === null}
        onClick={() => onChange(null)}
      />
      {channels.map((c) => (
        <Chip
          key={c}
          label={CHANNEL_LABELS[c]}
          icon={<ChannelIcon channel={c} size={12} className="shrink-0" />}
          active={value === c}
          onClick={() => onChange(c)}
        />
      ))}
    </div>
  );
}

interface ChipProps {
  label: string;
  icon?: React.ReactNode;
  active: boolean;
  onClick: () => void;
}

function Chip({ label, icon, active, onClick }: ChipProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-space-1 px-space-3 py-[6px] rounded-pill",
        "font-display text-[10px] font-bold uppercase tracking-[0.12em] border",
        "transition-colors duration-fast ease-standard",
        active
          ? "bg-gold text-onyx border-gold"
          : "bg-white-pure text-gray-mid border-border-light hover:bg-white-warm",
      )}
    >
      {icon}
      {label}
    </button>
  );
}
